/**
 * ELF-CIRCLE — Sponsorship & Partners.
 *
 * The company overview is explicit that the two owned properties have
 * different buyers: Made In New Jersey sells to municipal, tourism and
 * cultural bodies, while the monthly networking series sells to brand
 * sponsors, investors and industry partners. Sharing one "Book a consultation"
 * queue between them loses that distinction the moment a lead lands, so this
 * page keeps them apart all the way through — including the `source` each CTA
 * carries into the CRM.
 *
 * Signature mechanism: the track switch. One segmented control swaps the whole
 * ledger — buyer, cadence, scale, entry point and tier stack — with the rail
 * sliding between the two rather than the page re-rendering as a new screen,
 * so the comparison stays legible as a single object with two states.
 *
 * Static page — imagery is bundled rather than an editable CMS slot.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ElfFooter from '../ElfFooter';
import ElfPageNav, { ElfCrumb } from '../ElfPageNav';
import ElfShell from '../ElfShell';
import { useConsult } from '../elfConsult';
import { route } from '../../../../../shared/utils';
import handshakeArt from '../../../../../assets/elf/sponsorship-handshake.jpg';

type TrackId = 'annual' | 'monthly';

interface Tier {
  name: string;
  price: string;
  unit: string;
  featured?: boolean;
  perks: [string, boolean][];
}

interface Track {
  id: TrackId;
  tab: string;
  property: string;
  cadence: string;
  scale: string;
  buyer: string;
  entry: string;
  lede: string;
  /** CRM source stem — the two tracks must never share a pipeline. */
  source: string;
  tiers: Tier[];
}

const TRACKS: Track[] = [
  {
    id: 'annual',
    tab: 'Annual — Made In New Jersey',
    property: 'Made In New Jersey Festival',
    cadence: 'Annual',
    scale: 'Statewide · 21 counties',
    buyer: 'Municipal, tourism & cultural bodies',
    entry: 'Season-long commitment',
    lede:
      'A statewide Arts & Culture initiative across all 21 New Jersey counties, produced annually by ELF-CIRCLE. Partners here are county arts councils, tourism boards and statewide sponsors buying cultural presence across a season, not an impression count.',
    source: 'sponsorship.annual',
    tiers: [
      {
        name: 'County',
        price: 'Tier I',
        unit: 'single-county programming',
        perks: [
          ['Named presence in one county’s programming', true],
          ['Logo on county-level collateral', true],
          ['Local press inclusion', true],
          ['Statewide broadcast presence', false],
          ['Main-stage naming', false],
        ],
      },
      {
        name: 'Regional',
        price: 'Tier II',
        unit: 'North / Central / Shore / South',
        featured: true,
        perks: [
          ['Named presence across a full region', true],
          ['Regional broadcast and press inclusion', true],
          ['On-site activation footprint', true],
          ['Partner listing on the festival hub', true],
          ['Main-stage naming', false],
        ],
      },
      {
        name: 'Statewide',
        price: 'Tier III',
        unit: 'all 21 counties',
        perks: [
          ['Presenting presence across all 21 counties', true],
          ['Main-stage and hub naming', true],
          ['First-party audience data share', true],
          ['Year-round partner recognition', true],
          ['Category exclusivity', true],
        ],
      },
    ],
  },
  {
    id: 'monthly',
    tab: 'Monthly — Networking Series',
    property: 'Monthly Networking Events',
    cadence: 'Monthly',
    scale: 'National · rotating launch markets',
    buyer: 'Brand sponsors, investors & industry partners',
    entry: 'Get in the room first',
    lede:
      'A recurring room of creatives across entertainment, lifestyle and fashion, rotating through the launch markets. Monthly access is the lower-commitment entry point — sponsors meet the market before committing to a statewide season.',
    source: 'sponsorship.monthly',
    tiers: [
      {
        name: 'Room',
        price: 'Tier I',
        unit: 'one market, one night',
        perks: [
          ['Named host presence for one event', true],
          ['Table or activation corner', true],
          ['Attendee list summary post-event', true],
          ['Multi-market rotation', false],
          ['Series naming', false],
        ],
      },
      {
        name: 'Circuit',
        price: 'Tier II',
        unit: 'four markets, one quarter',
        featured: true,
        perks: [
          ['Presence across a quarter of the rotation', true],
          ['Speaking slot in each market', true],
          ['Co-branded invitations', true],
          ['Introductions brokered by the ELF team', true],
          ['Series naming', false],
        ],
      },
      {
        name: 'Series',
        price: 'Tier III',
        unit: 'twelve months, every market',
        perks: [
          ['Series naming across the national calendar', true],
          ['Presence in every launch market', true],
          ['First look at Made In New Jersey inventory', true],
          ['Investor and patron introductions', true],
          ['Category exclusivity', true],
        ],
      },
    ],
  },
];

/** The comparison ledger — same rows, different column per track. */
const LEDGER: [string, keyof Pick<Track, 'cadence' | 'scale' | 'buyer' | 'entry'>][] = [
  ['Cadence', 'cadence'],
  ['Scale', 'scale'],
  ['Who it is for', 'buyer'],
  ['Commitment', 'entry'],
];

export const Sponsorship: React.FC = () => {
  const consult = useConsult();
  const [active, setActive] = useState<TrackId>('annual');
  const track = TRACKS.find((t) => t.id === active) ?? TRACKS[0];
  const other = TRACKS.find((t) => t.id !== active) ?? TRACKS[1];

  const enquire = (source: string, title: string) => () =>
    consult?.open({ source, interest: 'Sponsorship', title });

  return (
    <ElfShell>
      <div className="elf-page">
        <div className="elf-page__panel">
          <ElfPageNav current="/sponsorship" />
          <div style={{ padding: '18px var(--pgut) 0' }}>
            <ElfCrumb trail={[{ label: 'Home', to: '/' }, 'Sponsorship']} />
          </div>

          {/* --- thesis --- */}
          <div className="spon-head">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <span className="eyebrow" style={{ alignSelf: 'flex-start' }}>
                <i />
                Two owned properties, two pipelines
              </span>
              <h1 className="elf-page__h1">
                Back the circle —<br />
                <span style={{ color: 'var(--acc-panel)' }}>on the cadence that fits.</span>
              </h1>
              <p className="wbody" style={{ maxWidth: '60ch' }}>
                ELF-CIRCLE owns both properties outright, and they sell to different rooms. Pick the
                track and the whole page follows — audience, scale, commitment and tiers.
              </p>
            </div>
            <div className="spon-head__art">
              <img src={handshakeArt} alt="Two people shaking hands across a meeting table" />
            </div>
          </div>

          {/* --- signature: the track switch --- */}
          <div className="spon-track">
            <div className="spon-switch" role="tablist" aria-label="Sponsorship tracks">
              <span className="spon-switch__rail" data-at={active} aria-hidden="true" />
              {TRACKS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={t.id === active}
                  className="spon-switch__tab"
                  onClick={() => setActive(t.id)}
                >
                  <span className="spon-switch__long">{t.tab}</span>
                  <span className="spon-switch__short">{t.cadence}</span>
                </button>
              ))}
            </div>

            <div className="spon-panel" key={track.id}>
              <div className="spon-panel__lead">
                <span className="colhead">{track.property}</span>
                <p className="wbody" style={{ fontSize: 15.5, marginTop: 12 }}>
                  {track.lede}
                </p>
                <button
                  type="button"
                  className="pillcta mag"
                  style={{ marginTop: 22, alignSelf: 'flex-start' }}
                  onClick={enquire(`${track.source}.lead`, `${track.property} — sponsorship`)}
                >
                  Enquire about {track.cadence.toLowerCase()} sponsorship <span>→</span>
                </button>
              </div>

              <dl className="spon-ledger">
                {LEDGER.map(([label, field]) => (
                  <div className="spon-ledger__row" key={label}>
                    <dt className="statl">{label}</dt>
                    <dd className="spon-ledger__val">{track[field]}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="spon-tiers">
              {track.tiers.map((tier) => (
                <div className={tier.featured ? 'ptier featured' : 'ptier'} key={`${track.id}-${tier.name}`}>
                  <div className="elf-ptier__head">
                    <span className="elf-ptier__name">{tier.name}</span>
                    {tier.featured && <span className="statl">Most taken</span>}
                  </div>
                  <div className="elf-ptier__price">
                    <span>{tier.price}</span>
                    <span>{tier.unit}</span>
                  </div>
                  <ul>
                    {tier.perks.map(([perk, on]) => (
                      <li className={on ? undefined : 'off'} key={perk}>
                        <b>{on ? '✓' : '—'}</b>
                        {perk}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className={tier.featured ? 'pillcta mag' : 'pillghost'}
                    onClick={enquire(
                      `${track.source}.tier.${tier.name.toLowerCase()}`,
                      `${track.property} — ${tier.name} tier`,
                    )}
                  >
                    Request the kit {tier.featured && <span>→</span>}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* --- the escalation path --- */}
          <div className="spon-ladder">
            <span className="colhead">The path most partners take</span>
            <div className="spon-ladder__rows">
              {[
                ['01', 'Get in the room', 'One monthly event in a single market — the lowest-commitment way to see the audience firsthand.'],
                ['02', 'Ride the circuit', 'A quarter of the national rotation, with a speaking slot and introductions brokered in each market.'],
                ['03', 'Take the season', 'A region — or all 21 counties — of Made In New Jersey, with naming and first-party data.'],
              ].map(([n, head, body]) => (
                <div className="spon-step" key={n}>
                  <span className="wnum">{n}</span>
                  <span className="whead">{head}</span>
                  <p className="wbody" style={{ fontSize: 14.5 }}>
                    {body}
                  </p>
                </div>
              ))}
            </div>
            <p className="wtag" style={{ fontSize: 13, marginTop: 4 }}>
              Recurring monthly access is a different — and usually easier — entry point than an
              annual statewide commitment. Partners are welcome to start at step three.
            </p>
          </div>

          <div
            className="elf-page__grid2"
            style={{
              padding: '0 var(--pgut) 72px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              borderTop: '1px solid rgba(var(--panel-fg-rgb),.1)',
            }}
          >
            <Link
              className="nextlink"
              to={route(other.id === 'annual' ? '/made-in-new-jersey' : '/networking-events')}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">The other property</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  {other.property}
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  {other.scale}
                </span>
              </div>
              <span className="arw">→</span>
            </Link>
            <button
              type="button"
              className="nextlink"
              onClick={enquire('sponsorship.kit', 'Request the media kit')}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">Numbers first</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Request the media kit
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Audience, reach and inventory, both properties.
                </span>
              </div>
              <span className="arw">→</span>
            </button>
          </div>
        </div>
      </div>
      <ElfFooter />
    </ElfShell>
  );
};

export default Sponsorship;
